import { t } from "i18next";
import {
  ApmApiMethods,
  BooleanStringEnum,
  PeriodEnum,
  PlatformTypeEnum,
  ApmTimeRange,
  TimeUnitEnum,
  ApmInterval,
  NullabilityEnum,
  DBPlatformEnum,
} from "utils/enums";

export const PAGE_OPTIONS = {
  DATA: [
    {
      value: 10,
      label: 10,
    },
  
    {
      value: 50,
      label: 50,
    },
    {
      value: 100,
      label: 100,
    },
  ],

  DEFAULT_VALUE: 10,
  DEFAULT_SIZE: "middle",

  PAGINATION: {
    current: 1,
    pageSize: 10,
    simple: false,
  },
};

export const TABLE_OPTIONS = {
  SIZE: "small",
};

export const STRING_BOOLEAN_OPTIONS = {
  TRUE: "true",
  FALSE: "false",
  BOOLEAN:"boolean"

};

export const BOOLEAN_OPTIONS = {
  TRUE: true,
  FALSE: false,
};

export const APNS_FILE_TYPE_OPTIONS = {
  P8: "p8",
  P12: "p12",
};

export const MODE_OPTIONS = {
  DEV: "dev",
  PROD: "prod",
};
export const E_OPERATOR_OPTIONS = [
  { label: "AND", 
    value: "AND" },
  {
    label: "OR",
    value: "OR",
  },
];
export const platformOptions = (includeAll=false)=>[
  ...(includeAll?[
    {value:PlatformTypeEnum.ALL,
     label:t("allLabel")
    }
  ]:[]),
  {
    value: PlatformTypeEnum.ANDROID,
    label: t("androidLabel"),
  },
  {
    value: PlatformTypeEnum.IOS,
    label: t("iosLabel"),
  },
  {
    value: PlatformTypeEnum.WEB,
    label: t("webLabel"),
  },
];
export const timeUnitOptions=()=>[
  {
    label: t("secondsLabel"),
    value: TimeUnitEnum.SECONDS,
  },
  {
    label: t("minutesLabel"),
    value: TimeUnitEnum.MINUTES,
  },
  {
    label: t("hoursLabel"),
    value: TimeUnitEnum.HOURS,
  },
  {
    label: t("dayLabel"),
    value: TimeUnitEnum.DAYS,
  },
  {
    label: t("weekLabel"),
    value: TimeUnitEnum.WEEKS,
  },
  {
    label: t("monthLabel"),
    value: TimeUnitEnum.MONTHS,
  },
]

export const periodOptions=(isAudience:boolean=false)=>[
  {
    label: t("yesterdayLabel"),
    value: PeriodEnum.YESTERDAY,
  },
  {
    label: t("todayLabel"),
    value: PeriodEnum.TODAY,
  },
  {
    label: t("tomorrowLabel"),
    value: PeriodEnum.TOMORROW,
  },...(!isAudience
    ? [
        {
          label: t("thisWeekLabel"),
          value: PeriodEnum.THIS_WEEK,
        },
        {
          label: t("lastWeekLabel"),
          value: PeriodEnum.LAST_WEEK,
        },
        {
          label: t("thisMonthLabel"),
          value: PeriodEnum.THIS_MONTH,
        },
        {
          label: t("lastMonthLabel"),
          value: PeriodEnum.LAST_MONTH,
        },
        {
          label: t("customLabel"),
          value: PeriodEnum.CUSTOM,
        },
      ]
    : []),
];

export const booleanStringOptions = () => [
  {
    label: t("trueLabel"),
    value: BooleanStringEnum.TRUE,
  },
  {
    label: t("falseLabel"),
    value: BooleanStringEnum.FALSE,
  },
];

export const apmTimeRange = {
  statsPeriod: {
    options: [
      { label: "Last hour", value: ApmTimeRange.LAST_1_HOUR },
      { label: "Last 24 hours", value: ApmTimeRange.LAST_24_HOURS },
      { label: "Last 7 days", value: ApmTimeRange.LAST_7_DAYS },
      { label: "Last 14 days", value: ApmTimeRange.LAST_14_DAYS },
      { label: "Last 30 days", value: ApmTimeRange.LAST_30_DAYS },
      { label: "Last 90 days", value: ApmTimeRange.LAST_90_DAYS },
    ],
    default: ApmTimeRange.LAST_14_DAYS,
  },
  interval: {
    [ApmApiMethods.SESSIONS]: {
      [ApmTimeRange.LAST_1_HOUR]: ApmInterval.ONE_MINUTE,
      [ApmTimeRange.LAST_24_HOURS]: ApmInterval.ONE_HOUR,
      [ApmTimeRange.LAST_7_DAYS]: ApmInterval.ONE_HOUR,
      [ApmTimeRange.LAST_14_DAYS]: ApmInterval.ONE_HOUR,
      [ApmTimeRange.LAST_30_DAYS]: ApmInterval.TWO_HOURS,
      [ApmTimeRange.LAST_90_DAYS]: ApmInterval.SIX_HOURS,
    },
    [ApmApiMethods.EVENT_STATS]: {
      [ApmTimeRange.LAST_1_HOUR]: ApmInterval.ONE_MINUTE,
      [ApmTimeRange.LAST_24_HOURS]: ApmInterval.ONE_HOUR,
      [ApmTimeRange.LAST_7_DAYS]: ApmInterval.TWELVE_HOURS,
      [ApmTimeRange.LAST_14_DAYS]: ApmInterval.ONE_DAY,
      [ApmTimeRange.LAST_30_DAYS]: ApmInterval.ONE_DAY,
      [ApmTimeRange.LAST_90_DAYS]: ApmInterval.ONE_DAY,
    },
  },
};
export function getApmInterval(
  timeRange: string | { start: string; end: string },
  apiMethod: ApmApiMethods
) {
  // Preset case (e.g. "7d", "1h")
  if (typeof timeRange === "string") {
    return apmTimeRange.interval?.[apiMethod]?.[timeRange] ?? ApmInterval.ONE_HOUR;
  }

  // Custom range → calculate days
  const start = new Date(timeRange.start);
  const end = new Date(timeRange.end);
  const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

  // Match to closest preset
  const preset =
    diffDays <= 1
      ? ApmTimeRange.LAST_24_HOURS
      : diffDays <= 7
      ? ApmTimeRange.LAST_7_DAYS
      : diffDays <= 14
      ? ApmTimeRange.LAST_14_DAYS
      : diffDays <= 30
      ? ApmTimeRange.LAST_30_DAYS
      : ApmTimeRange.LAST_90_DAYS;

  return apmTimeRange.interval?.[apiMethod]?.[preset] ?? ApmInterval.ONE_HOUR;
}

export function getApmTimeRange(
  timeRange: string | { start: string; end: string },
  isPreset: boolean
) {
  return isPreset
    ? { statsPeriod: timeRange }
    : { start: timeRange.start, end: timeRange.end, utc: true,    };
}

export const NullabilityOptions=()=>[
  { label: t("nullLabel"), value: NullabilityEnum.NULL },
  { label: t("notNullLabel"), value: NullabilityEnum.NOT_NULL },
];

export const dbPlatformOptions = () => [
    {
      value: DBPlatformEnum.ANDROID,
      label: t("androidLabel"),
    },
    {
      value: DBPlatformEnum.IOS,
      label: t("iosLabel"),
    },
    {
      value: DBPlatformEnum.WEB,
      label: t("webLabel"),
    }
];
export const emailEditorModes = ()=>[
  { label: t("editorLabel"), 
    value: BooleanStringEnum.FALSE },
  {
    label: t("htmlLabel"),
    value: BooleanStringEnum.TRUE,
  },
];
